import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Phone, Calculator, X, ArrowLeft } from 'lucide-react';
import { MaterialCalculator } from '../components/MaterialCalculator';
import { blogPosts } from '../data/blog';
import { JsonLd, useSEO } from '../utils/seo';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [showCalcModal, setShowCalcModal] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const post = blogPosts.find(p => p.slug === slug);
  useSEO({
    title: post ? `${post.title} | 206Gravel` : 'Gravel Guides | 206Gravel',
    description: post?.excerpt || 'Practical guides for gravel, driveways, drainage, and bulk material delivery across the Puget Sound area.',
    image: post?.image,
    path: post ? `/blog/${post.slug}` : '/blog'
  });
  if (!post) {
    return <Navigate to="/blog" replace data-id="element-1985" data-mp="114ke9en5flql" />;
  }
  return <main className="w-full bg-[#fdfbf7] dark:bg-[#2a2a2a] transition-colors duration-300 min-h-screen" data-id="element-1986" data-mp="113isaf4lheu3">
      <JsonLd data={{
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      mainEntityOfPage: `https://206gravel.com/blog/${post.slug}`,
      author: {
        '@type': 'Organization',
        name: '206Gravel'
      },
      publisher: {
        '@type': 'Organization',
        name: '206Gravel'
      }
    }} data-id="element-1987" data-mp="165gglnruqztw" />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#333333] overflow-hidden" data-id="element-1988" data-mp="1b3hvdwi8wczk">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{
        backgroundImage: `url(${post.image})`
      }} data-id="element-1989" data-mp="1g2cr1z1b9w13" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#333333]/40 to-[#333333]/80" data-id="element-1990" data-mp="23rnmd0zw6y7u" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1991" data-mp="28ivs9n20k1g9">
          <Link to="/blog" className="inline-flex items-center text-brand-orange hover:text-white transition-colors mb-6 font-bold text-sm uppercase tracking-widest" data-id="element-1992" data-mp="2if5s82p6xyvu">
            <ArrowLeft className="w-4 h-4 mr-1" data-id="element-1993" data-mp="2ddoswbmcukhd" />
            Back to Blog
          </Link>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6 uppercase" data-id="element-1994" data-mp="28ij6lbp1j3sr">
            {post.title}
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-lg text-stone-300 font-medium font-sub max-w-2xl mx-auto" data-id="element-1995" data-mp="1umypmlsvgvnz">
            {post.date}
          </motion.p>
        </div>
      </section>

      {post.type === 'guide' ? <>
          {/* Quick Info Bar */}
          {post.quickInfo && <section className="bg-[#3a3a3a] border-y border-[#4a4a4a] py-5" data-id="element-1996" data-mp="3ndsw5ftzt9yf">
              <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-x-10 gap-y-3 text-sm text-stone-300 font-medium" data-id="element-1997" data-mp="3nfvjtt7oaqzz">
                {post.quickInfo.map((info, i) => <span key={i} data-id="element-1998" data-mp="9co80wrfg9s" data-mpi={i}>{info}</span>)}
              </div>
            </section>}

          {/* Materials You'll Need */}
          {post.materialsNeeded && <section className="py-16 border-b border-stone-200 dark:border-white/5" data-id="element-1999" data-mp="33ptm677966u2">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2000" data-mp="38omnz2d6g9ee">
                <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-2001" data-mp="38owecr24wikm">
                  <h2 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-2002" data-mp="3ig97dlzo5es8">
                    Materials You'll Need
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2003" data-mp="2pwkgohmfe5gt">
                    {post.materialsNeeded.map(item => <div key={item.name} className="flex items-start gap-3 p-4 bg-white dark:bg-[#373737] rounded-xl border border-stone-200 dark:border-white/5" data-id="element-2004" data-mp="vi6i4hh2ika8">
                        <CheckCircle2 className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-2005" data-mp="158rr8yudb77e" />
                        <div data-id="element-2006" data-mp="10griqi08f5z7">
                          {item.slug ? <Link to={`/shop/${item.slug}`} className="font-bold text-brand-orange hover:underline text-sm" data-id="element-2007" data-mp="10ewyz70p5jwp">
                              {item.name}
                            </Link> : <p className="font-bold text-brand-charcoal dark:text-white text-sm" data-id="element-2008" data-mp="1a61gadduhgup">
                              {item.name}
                            </p>}
                          <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-2009" data-mp="1j79b2lh5jzk1">
                            {item.use}
                          </p>
                        </div>
                      </div>)}
                  </div>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3" data-id="element-2010" data-mp="3l0p2hkl2dig2">
                    <button onClick={() => setShowCalcModal(true)} className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm rounded-none transition-colors" data-id="element-2011" data-mp="20hv3qhxe5g3i">
                      <Calculator className="w-4 h-4" data-id="element-2012" data-mp="1rmp2lw92fm7b" />
                      Calculate How Much You Need
                    </button>
                    <Link to="/#materials" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 border border-stone-300 dark:border-[#555555] text-brand-charcoal dark:text-white font-bold text-sm rounded-none hover:bg-stone-100 dark:hover:bg-[#444444] transition-colors" data-id="element-2013" data-mp="1hpsrrjvp2nzl">
                      Browse Our Catalog
                    </Link>
                  </div>
                </motion.div>
              </div>
            </section>}

          {/* Steps */}
          {post.steps && <section className="py-16" data-id="element-2014" data-mp="37509818u6zu8">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2015" data-mp="373z1a5n4atgq">
                <div className="space-y-20" data-id="element-2016" data-mp="jxcej7qeei88">
                  {post.steps.map(step => <motion.article key={step.number} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} className="relative" data-id="element-2017" data-mp="expykqj6qnvq">
                      <div className="flex items-center gap-4 mb-6" data-id="element-2018" data-mp="12nas23cyt3j9">
                        <span className="flex items-center justify-center w-10 h-10 bg-brand-orange text-white font-heading font-extrabold text-lg rounded-none shrink-0" data-id="element-2019" data-mp="12mj1x824zy0d">
                          {step.number}
                        </span>
                        <h3 className="font-heading text-2xl md:text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-2020" data-mp="owfhmmmtiedk">
                          {step.title}
                        </h3>
                      </div>

                      <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-6" data-id="element-2021" data-mp="1hjjs374ojkwq">
                        <img src={step.image} alt={step.title} className="w-full h-full object-cover" data-id="element-2022" data-mp="1owj1k4wl6nqf" />
                      </div>

                      <p className="text-brand-charcoal/85 dark:text-stone-300 text-base leading-relaxed mb-6" data-id="element-2023" data-mp="1tvccbpkho0mw">
                        {step.description}
                      </p>

                      <div className="bg-stone-50 dark:bg-[#333333] border border-stone-200 dark:border-white/5 rounded-xl p-5" data-id="element-2024" data-mp="22wa8xmsll5vg">
                        <p className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider mb-3" data-id="element-2025" data-mp="27shhlncdovh3">
                          Pro Tips
                        </p>
                        <ul className="space-y-2" data-id="element-2026" data-mp="2hl6qqbkghx99">
                          {step.tips.map((tip, i) => <li key={i} className="flex items-start gap-2 text-sm text-brand-charcoal/80 dark:text-stone-300" data-id="element-2027" data-mp="2mi6kpo4179k0" data-mpi={i}>
                              <CheckCircle2 className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" data-id="element-2028" data-mp="2qmwdv57b2a18" />
                              {tip}
                            </li>)}
                        </ul>
                      </div>

                      {step.material && <div className="mt-4 flex items-center gap-3 p-4 bg-brand-orange/10 border border-brand-orange/20 dark:border-white/5 rounded-xl" data-id="element-2029" data-mp="3bwjkcd6dgke8">
                          <span className="text-sm font-bold text-brand-orange" data-id="element-2030" data-mp="3qsj089zq3otx">
                            Recommended:
                          </span>
                          <Link to={`/shop/${step.materialSlug}`} className="text-sm text-brand-charcoal dark:text-white font-medium hover:text-brand-orange transition-colors" data-id="element-2031" data-mp="2y3u0aeaelpjr">
                            {step.material}
                          </Link>
                          <Link to={`/shop/${step.materialSlug}`} className="ml-auto text-xs font-bold text-brand-orange hover:underline" data-id="element-2032" data-mp="37w9gb6254420">
                            View Product →
                          </Link>
                        </div>}
                    </motion.article>)}
                </div>

                {/* SEO Blocks */}
                {post.seoBlocks && post.seoBlocks.map((block, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className={`mt-${idx === 0 ? '20' : '12'} p-6 md:p-8 ${idx % 2 === 0 ? 'bg-white dark:bg-[#333333]' : 'bg-stone-50 dark:bg-[#373737]'} border border-stone-200 dark:border-white/5 rounded-xl`} data-id="element-2033" data-mp="pux7wr36496h" data-mpi={idx}>
                      <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-2034" data-mp="1dmkreny3ohyq">
                        {block.title}
                      </h3>
                      {block.content}
                    </motion.div>)}
              </div>
            </section>}
        </> /* Standard Article Layout */ : <section className="py-16" data-id="element-2035" data-mp="3kcsnp3c3pmqi">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2036" data-mp="3kbhbqg8i58ob">
            {post.content}
          </div>
        </section>}

      {/* CTA */}
      <section className="py-16 bg-[#333333] border-t border-[#4a4a4a]" data-id="element-2037" data-mp="3d6e89pl50ymc">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2038" data-mp="3d8gurzn309mb">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-2039" data-mp="3i4pdfn0hmky8">
            Ready to Get Started?
          </h2>
          <p className="text-stone-300 font-medium font-sub mb-8 max-w-lg mx-auto" data-id="element-2040" data-mp="38f5xoq82n77l">
            We deliver gravel, crushed rock, and all the materials you need
            straight to your driveway. Same-day delivery available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2041" data-mp="2zfisrug4vj9s">
            <Link to="/#materials" className="px-8 py-3.5 bg-brand-orange hover:bg-brand-orange-hover text-white font-bold rounded-none transition-colors flex items-center justify-center" data-id="element-2042" data-mp="2lfq74djgrt8o">
              Order Materials
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-2043" data-mp="jowo1ga9otvx" />
            </Link>
            <a href="tel:206-619-3742" className="px-8 py-3.5 border border-white/30 bg-white/10 text-white font-bold rounded-none hover:bg-white hover:text-stone-900 transition-all flex items-center justify-center" data-id="element-2044" data-mp="npovq53hqqq3">
              <Phone className="w-4 h-4 mr-2" data-id="element-2045" data-mp="246nnpnqgrnhh" />
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>

      {/* Calculator Modal */}
      <AnimatePresence data-id="element-2046" data-mp="2d6kgo1jvz8ic">
        {showCalcModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2047" data-mp="2d5itwzticxks">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowCalcModal(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-2048" data-mp="2ru5fvkdn4nkz" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="relative w-full max-w-4xl bg-[#474747] border border-[#555555] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" data-id="element-2049" data-mp="30tirojp8lo8v">
              <div className="flex items-center justify-between p-6 border-b border-[#555555] shrink-0" data-id="element-2050" data-mp="36k3j7v6qf62o">
                <h3 className="text-xl font-bold text-white flex items-center" data-id="element-2051" data-mp="3bjg7mkd94l7d">
                  <Calculator className="w-5 h-5 mr-2 text-brand-orange" data-id="element-2052" data-mp="3blkjbohe6oi7" />
                  Material Calculator
                </h3>
                <button onClick={() => setShowCalcModal(false)} className="text-stone-400 hover:text-white transition-colors p-1" data-id="element-2053" data-mp="3kko735ri7s31">
                  <X className="w-6 h-6" data-id="element-2054" data-mp="3phemdqvhijne" />
                </button>
              </div>
              <div className="overflow-y-auto" data-id="element-2055" data-mp="3ub0owq7xp75h">
                <MaterialCalculator variant="modal" data-id="element-2056" data-mp="33epj9s8zquf" />
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </main>;
}